import { useMantineTheme, Box, Button, Flex, MultiSelect } from "@mantine/core";
import { useContext, ReactNode, memo, useCallback } from "react";
import { AppContext } from "src/api/AppContext";
import { HeightCard } from "src/utils/constants";
import ActionCard from "src/components/ActionCard";
import { AiOutlineCalendar } from "react-icons/ai";
import { IoIosArrowBack } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import { getDateRanges } from "src/utils/dateHelper";
import { IChartFilter } from "src/api/context/DashboardContext";

type IDashboardCard = {
  children: ReactNode;
  isChartDetail?: boolean;
  filters: IChartFilter;
  setFilters: (obj: IChartFilter) => void;
  getDateRangeText: () => void;
  handleModal: () => void;
  handleLocChange?: (locations: string[]) => void;
  rightIconButton?: ReactNode;
  showQuickSelectOption?: boolean;
};

function DashboardCard({
  filters,
  setFilters,
  getDateRangeText,
  handleModal,
  handleLocChange,
  isChartDetail = false,
  children,
  rightIconButton,
  showQuickSelectOption = false,
}: IDashboardCard) {
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const { currentOrganization } = useContext(AppContext);

  const handleBack = () => {
    navigate(-1);
  };

  const defaultOptions = useCallback(() => {
    return (
      <>
        <Button
          radius="xl"
          variant="outline"
          size="sm"
          styles={{
            root: {
              background: theme.colors.primary[3],
              color: theme.colors.primaryTextColor[3],
              height: 30,
              borderColor: theme.colors.secondary[6],
              "&:hover": {
                background: theme.colors.primary[6],
                color: theme.colors.primaryTextColor[6],
              },
            },
          }}
          onClick={handleModal}
          rightIcon={<AiOutlineCalendar size="1rem" />}
        >
          {getDateRangeText()}
        </Button>

        <Button
          radius="xl"
          variant="outline"
          size="sm"
          styles={{
            root: {
              background: theme.colors.primary[3],
              color: theme.colors.primaryTextColor[3],
              height: 30,
              borderColor: theme.colors.secondary[6],
              "&:hover": {
                background: theme.colors.primary[6],
                color: theme.colors.primaryTextColor[6],
              },
            },
          }}
          onClick={() => {
            setFilters({
              ...filters,
              dateRange: [
                new Date(new Date().setHours(0, 0, 0, 0)),
                new Date(new Date().setHours(23, 59, 59, 999)),
              ],
            });
          }}
        >
          Today
        </Button>

        <Button
          radius="xl"
          variant="outline"
          size="sm"
          styles={{
            root: {
              background: theme.colors.primary[3],
              color: theme.colors.primaryTextColor[3],
              height: 30,
              borderColor: theme.colors.secondary[6],
              "&:hover": {
                background: theme.colors.primary[6],
                color: theme.colors.primaryTextColor[6],
              },
            },
          }}
          onClick={() => {
            setFilters({
              ...filters,
              dateRange: [
                new Date(new Date().setHours(-24, 0, 0, 0)),
                new Date(new Date().setHours(0, 0, 0, -1)),
              ],
            });
          }}
        >
          Yesterday
        </Button>

        <Button
          radius="xl"
          variant="outline"
          size="sm"
          styles={{
            root: {
              background: theme.colors.primary[3],
              color: theme.colors.primaryTextColor[3],
              height: 30,
              borderColor: theme.colors.secondary[6],
              "&:hover": {
                background: theme.colors.primary[6],
                color: theme.colors.primaryTextColor[6],
              },
            },
          }}
          onClick={() => {
            setFilters({
              ...filters,
              dateRange: [
                new Date(new Date(new Date().setDate(1)).setHours(0, 0, 0, 0)),
                new Date(new Date().setHours(23, 59, 59, 999)),
              ],
            });
          }}
        >
          Month-to-Day
        </Button>
      </>
    );
  }, [getDateRangeText]);

  const quickOptions = useCallback(() => {
    return (
      <>
        <Button
          radius="xl"
          variant="outline"
          size="sm"
          styles={{
            root: {
              background: theme.colors.primary[3],
              color: theme.colors.primaryTextColor[3],
              height: 30,
              borderColor: theme.colors.secondary[6],
              "&:hover": {
                background: theme.colors.primary[6],
                color: theme.colors.primaryTextColor[6],
              },
            },
          }}
          onClick={handleModal}
          rightIcon={<AiOutlineCalendar size="1rem" />}
        >
          {getDateRangeText()}
        </Button>

        <Button
          radius="xl"
          variant="outline"
          size="sm"
          styles={{
            root: {
              background: theme.colors.primary[3],
              color: theme.colors.primaryTextColor[3],
              height: 30,
              borderColor: theme.colors.secondary[6],
              "&:hover": {
                background: theme.colors.primary[6],
                color: theme.colors.primaryTextColor[6],
              },
            },
          }}
          onClick={() => {
            setFilters({
              ...filters,
              dateRange: getDateRanges().thisWeek as [Date, Date],
            });
          }}
        >
          This Week
        </Button>

        <Button
          radius="xl"
          variant="outline"
          size="sm"
          styles={{
            root: {
              background: theme.colors.primary[3],
              color: theme.colors.primaryTextColor[3],
              height: 30,
              borderColor: theme.colors.secondary[6],
              "&:hover": {
                background: theme.colors.primary[6],
                color: theme.colors.primaryTextColor[6],
              },
            },
          }}
          onClick={() => {
            setFilters({
              ...filters,
              dateRange: getDateRanges().nextWeek as [Date, Date],
            });
          }}
        >
          Next Week
        </Button>

        <Button
          radius="xl"
          variant="outline"
          size="sm"
          styles={{
            root: {
              background: theme.colors.primary[3],
              color: theme.colors.primaryTextColor[3],
              height: 30,
              borderColor: theme.colors.secondary[6],
              "&:hover": {
                background: theme.colors.primary[6],
                color: theme.colors.primaryTextColor[6],
              },
            },
          }}
          onClick={() => {
            setFilters({
              ...filters,
              dateRange: getDateRanges().thisMonth as [Date, Date],
            });
          }}
        >
          This Month
        </Button>
      </>
    );
  }, [getDateRangeText]);

  return (
    <>
      <ActionCard
        groupXs={{
          height: "auto",
          minHeight: HeightCard.lg,
        }}
        margin={isChartDetail ? "auto" : 0}
        title=""
        startIconButton={
          <Flex gap={8} align={"center"}>
            {isChartDetail && <IoIosArrowBack onClick={handleBack} />}

            {showQuickSelectOption ? quickOptions() : defaultOptions()}
          </Flex>
        }
        rightIconButton={
          rightIconButton || (
            <Box sx={{ minWidth: "25vw", maxWidth: "25vw", padding: "3px 0" }}>
              <MultiSelect
                placeholder="Locations"
                data={
                  currentOrganization?.locations?.map((loc) => ({
                    value: loc.id,
                    label: loc.name,
                  })) || []
                }
                searchable
                nothingFound="No Location Found"
                onChange={handleLocChange}
                value={filters.locations}
                sx={{
                  ".mantine-Input-wrapper": {
                    minHeight: 30,
                    maxHeight: "max-content",
                    minWidth: "20vw",
                  },
                  ".mantine-Input-input": {
                    borderRadius: 30,
                    minHeight: 30,
                    maxHeight: "max-content",
                  },
                  ".mantine-MultiSelect-values": {
                    input: { lineHeight: "inherit" },
                    minHeight: 28,
                  },
                }}
              />
            </Box>
          )
        }
      >
        {children}
      </ActionCard>
    </>
  );
}

export default memo(DashboardCard);
